import { useState } from 'react';
import { Card, Grid, Modal, SegmentedControl, Table, Text, Title } from '@mantine/core';
import aiGeneratedCat from '../../assets/images/ai-generated-cat.jpeg';
import aiGeneratedDog from '../../assets/images/ai-generated-dog.jpeg';
import aiGeneratedWoman from '../../assets/images/ai-generated-woman.jpeg';

const PromptGuideModal = ({ promptGuideModalOpen, setPromptGuideModalOpen }) => {
  const [selected, setSelected] = useState('text');

  const textGenerationTips = (
    <Card padding="md" radius="md" withBorder>
        <section>
          <Title order={4}>Set Goals</Title>
          <p>
            When creating a prompt for a text generating AI model, you have to clearly define your goal and provide
            sufficient details and context. This helps the AI understand what you want to achieve.
          </p>
        </section>
        <section>
          <Title order={4}>Action Verbs</Title>
          <p>
            To create a clear prompt, it is better to incorporate vrebs that will guide the AI model on exactly what to
            do. Here is a non exhaustive list of commonly used action verbs:{' '}
          </p>
          <ul>
            <li>
              <strong>Use action verbs: </strong>
            </li>
            <ul style={{ columnCount: 3, columnGap: 20, listStyleType: 'none', padding: 0, margin: 0 }}>
              <li>
                <strong>Create</strong>
              </li>
              <li>
                <strong>Draft</strong>
              </li>
              <li>
                <strong>Rephrase</strong>
              </li>
              <li>
                <strong>Write</strong>
              </li>
              <li>
                <strong>Summarize</strong>
              </li>
              <li>
                <strong>Include</strong>
              </li>
              <li>
                <strong>Describe</strong>
              </li>
              <li>
                <strong>Compare</strong>
              </li>
              <li>
                <strong>Combine</strong>
              </li>
              <li>
                <strong>Expand</strong>
              </li>
              <li>
                <strong>Explain</strong>
              </li>
              <li>
                <strong>Conclude</strong>
              </li>
              <li>
                <strong>Brainstorm</strong>
              </li>
            </ul>
          </ul>
        </section>
        <section>
          <Title order={4}>Provide Context</Title>
          <p>
            It is important to provide enough context for your specific use case. Simply imagine that you are explaining
            your scenario to another person for the first time. You can consider including:
          </p>
          <ul>
            <li>
              <strong>Topic:</strong> A description of your use case (e.g. "announcement of our brand new spring
              clothing collection").
            </li>
            <li>
              <strong>Audience:</strong> The generated content will be tailored to resonate with your target audience
              (e.g. "the busy on-the-go consumer").
            </li>
            <li>
              <strong>Brand Tone and Voice:</strong> The tone and voice in which your established brand communicates to
              your audience (e.g. "welcoming", "conversational").
            </li>
            <li>
              <strong>Brand Archetype:</strong> The human-like archetype your established brand matches the best (e.g.
              "The Caregiver").
            </li>
            <li>
              <strong>Custom Information:</strong> Any additional information or requirements that you want to be
              included in the content.{' '}
            </li>
          </ul>
        </section>
        <section>
          <Title order={4}>Things to Avoid</Title>
          <p>There are some key points that hinder AI's capabilities and performance and it is better to avoid them.</p>
          <ul>
            <li>
              <strong>Unnecessary Verbosity: </strong> Carefully consider your use case and provide details and context
              that will help for it specificlally. Incorporating details that are not related to your use case can
              confuse the AI model on what the main point is.
            </li>
            <li>
              <strong>Vague Requests: </strong> AI performs a lot better when provided with a specific description and
              requirements regarding the task at hand.
            </li>
            <li>
              <strong>Over-reliance:</strong> Avoid depending entirely on the AI for critical decisions or tasks. AI
              models can provide insights and suggestions, but human oversight is essential to ensure accuracy and
              appropriateness, especially for complex or sensitive issues.
            </li>
            <li>
              <strong>Lack of Understanding of AI Limitations:</strong> Do not assume that AI is flawless. Recognize its
              limitations, biases, and the extent of its training data.
            </li>
          </ul>
        </section>
    </Card>
  );

  const imageGenerationTips = (
    <Card padding="md" radius="md" withBorder>
        <section>
          <Title order={4}>Key Points</Title>
          <p>To optimise image quality and details, consider using these key points:</p>
          <ul>
            <li>
              <strong>Skin Textures:</strong> For detailed skin depictions.
            </li>
            <li>
              <strong>High Resolution:</strong> Ensures crystal-clear images ("High Resolution," "High-Resolution,"
              "High-Resolution Image").
            </li>
            <li>
              <strong>Cinematic:</strong> Adds a dynamic, film-like quality.
            </li>
          </ul>
        </section>
        <section>
          <Title order={4}>Crafting Your Prompt</Title>
          <p>Keep each part of your prompt clear and descriptive without unnecessary verbosity. Here’s how:</p>
          <ul>
            <li>
              <strong>Subject and Action</strong>
            </li>
            <ul>
              <li>
                <strong>Subject:</strong> The main focus (e.g., "Renaissance noblewoman," "vampire hunter").
              </li>
              <li>
                <strong>Action:</strong> Adds dynamism (e.g., "holding an ancient book").
              </li>
            </ul>
            <br />
            <li>
              <strong>Setting and Objects</strong>
            </li>
            <ul>
              <li>
                <strong>Environment/Setting:</strong> The background (e.g., "in a dimly lit Gothic castle").
              </li>
              <li>
                <strong>Object:</strong> Enhances the subject or story (e.g., "wearing an intricate lace collar").
              </li>
            </ul>
            <br />
            <li>
              <strong>Visual and Artistic Elements</strong>
            </li>
            <ul>
              <li>
                <strong>Color:</strong> Dominant color schemes (e.g., "shades of deep red and gold").
              </li>
              <li>
                <strong>Style:</strong> Artistic style (e.g., "reminiscent of Vermeer's lighting," "noir film").
              </li>
              <li>
                <strong>Mood/Atmosphere:</strong> Emotional quality (e.g., "atmosphere of mystery," "serene mood").
              </li>
              <li>
                <strong>Lighting:</strong> Specific lighting effects (e.g., "bathed in soft, natural window light").
              </li>
            </ul>
            <br />
            <li>
              <strong>Perspective and Textures</strong>
            </li>
            <ul>
              <li>
                <strong>Perspective/Viewpoint:</strong> Angle or perspective (e.g., "bird’s eye view," "from a low
                angle").
              </li>
              <li>
                <strong>Texture/Material:</strong> Visible textures (e.g., "rich velvet," "rough stone").
              </li>
            </ul>
            <br />
            <li>
              <strong>Time Period and Culture</strong>
            </li>
            <ul>
              <li>
                <strong>Time Period:</strong> Specific era (e.g., "Victorian Era," "futuristic 22nd century").
              </li>
              <li>
                <strong>Cultural Elements:</strong> Reflecting specific traditions (e.g., "inspired by Norse
                mythology").
              </li>
            </ul>
            <br />
            <li>
              <strong>Emotions and Medium</strong>
            </li>
            <ul>
              <li>
                <strong>Emotion:</strong> Expressed emotion (e.g., "deep contemplation," "joyful demeanor").
              </li>
              <li>
                <strong>Medium:</strong> Artistic medium or detail level (e.g., "watercolor painting," "crisp digital
                rendering").
              </li>
            </ul>
            <li>
              <strong>Clothing</strong>
            </li>
            <ul>
              <li>Ensure subjects are clothed, specifying styles (e.g., "Victorian gown," "modern casual wear").</li>
            </ul>
          </ul>
        </section>
        <section>
          <Title order={4}>Negative Prompt</Title>
          <p>
            It is important to be aware that image generation models can sometimes produce content that can be
            considered Not Safe For Work (NSFW). To avoid that you can use negative prompts to set explicit
            restrictions:
          </p>
          <ul>
            <li>
              <strong>Explicit Restrictions:</strong> e.g. "no nudity", "no violence", "no vulgarity".
            </li>
          </ul>
        </section>
        <section>
          <Title order={4}>Example Prompts and Results</Title>
          <Table withTableBorder striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: '50%' }}>Example Prompt</Table.Th>
                <Table.Th style={{ width: '50%' }}>Generated Image</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              <Table.Tr>
                <Table.Td>
                  "Golden retriever in a lush green park with trees seen in the background, fur glistening under the
                  soft afternoon sunlight, colors vivid green and golden, style realistic, mood serene, lighting natural
                  and diffused, perspective close-up, texture soft fur and fresh grass, cultural elements joyful outdoor
                  activity, natural bokeh effect"
                </Table.Td>
                <Table.Td>
                  <img src={aiGeneratedDog} alt="Example 1" width="100%" />
                </Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>
                  "Orange cat enjoying her afternoon coffee, fur is soft and nicely combed, location Paris cafe, the
                  Eiffel tower can be seen in the background, style realistic, lighting natural, perspective close-up,
                  weather is sunny with no clouds in the sky, texture soft fur and wooden table, cultural elements calm
                  afternoon cafe activity, natural bokeh background"
                </Table.Td>
                <Table.Td>
                  <img src={aiGeneratedCat} alt="Example 2" width="100%" />
                </Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>
                  "Young woman reading a book in a cozy coffee shop, plants are positioned all around her making the
                  atmosphere natural, brunette hair cascading over her shoulders, colors warm browns and soft whites,
                  style candid photography, mood relaxed, lighting natural light streaming through a nearby window,
                  perspective over-the-shoulder, texture soft wool sweater and glossy wooden table, a cup of coffee is
                  placed near her on the table"
                </Table.Td>
                <Table.Td>
                  <img src={aiGeneratedWoman} alt="Example 3" width="100%" />
                </Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </section>
    </Card>
  );

  return (
    <Modal
      opened={promptGuideModalOpen}
      onClose={() => setPromptGuideModalOpen(false)}
      size="xl"
      title="Welcome to Releva's Essentials Guide to AI Prompting! *"
      styles={{ body: { overflowY: 'auto' } }}
    >
      <Card padding="md" radius="md" withBorder mb="md">
        <div style={{ padding: '0.5rem', textAlign: 'center' }}>
          <p style={{ margin: 0 }}>
            We encourage you to experiment and refine your prompts to find what suits you and your needs best! That
            said, in this guide, you will find tips and tricks designed to help you achieve your goals when working
            with Artificial Intelligence models, whether you're generating text or images.
          </p>
        </div>
      </Card>
      <div>
        <SegmentedControl
          value={selected ?? ''}
          onChange={(value) => setSelected(value)}
          data={[
            { label: 'Text Generation Guide', value: 'text' },
            { label: 'Image Generation Guide', value: 'image' },
          ]}
        />
      </div>
      {selected && (
        <div style={{ marginTop: 'var(--mantine-spacing-md)' }}>
          {selected === 'text' ? textGenerationTips : imageGenerationTips}
        </div>
      )}
      <Text ta="center" size="sm" c="dimmed" mt="md">
        * Please note that all AI models, including those generating text and images, are capable of making mistakes.
        These mistakes can include hallucinations, improper results, and biases. Additionally, there is a potential
        for the generation of toxic content or inappropriate material, such as nudity. It is important to use these
        models responsibly and to remain mindful of these limitations and risks.
      </Text>
    </Modal>
  );
};

export default PromptGuideModal;
